import React from 'react';
import { motion } from 'framer-motion';
import { Cpu, Globe, Zap, Check, X, Minus, Loader } from 'lucide-react';
import './WorkflowPanel.css';

/**
 * Live workflow view for the verification terminal.
 *
 * The whole DAG is drawn up front from the static graph the server publishes,
 * so the user can see which agents exist and which run concurrently before any
 * of them start. Each node then lights up as `agent_update` events arrive.
 * Nodes sharing a `lane` execute in parallel and are drawn on the same row.
 */

const KIND_ICON = { llm: Cpu, http: Globe, local: Zap };

const STATUS_ICON = {
  done: Check,
  failed: X,
  skipped: Minus,
  running: Loader,
};

const WorkflowPanel = ({ graph, agents, elapsed }) => {
  if (!graph?.nodes?.length) return null;

  // Group by lane so concurrent agents share a row.
  const lanes = new Map();
  graph.nodes.forEach((node) => {
    if (!lanes.has(node.lane)) lanes.set(node.lane, []);
    lanes.get(node.lane).push(node);
  });
  const laneKeys = [...lanes.keys()].sort((a, b) => a - b);

  const stateOf = (id) => agents?.[id] || { status: 'pending', detail: '', elapsed: 0 };

  const doneCount = graph.nodes.filter((n) =>
    ['done', 'skipped'].includes(stateOf(n.id).status)
  ).length;

  return (
    <div className="workflow-panel">
      <div className="workflow-head">
        <span className="workflow-title">Agent Workflow</span>
        <span className="workflow-meta">
          {doneCount}/{graph.nodes.length} agents
          {elapsed ? ` · ${elapsed}s` : ''}
        </span>
      </div>

      <div className="workflow-lanes">
        {laneKeys.map((lane) => {
          const row = lanes.get(lane);
          const parallel = row.length > 1;
          return (
            <div key={lane} className="workflow-lane">
              <div className="lane-rail">
                <span className="lane-index">{lane + 1}</span>
                {parallel && <span className="lane-parallel">∥</span>}
              </div>
              <div className={`lane-nodes ${parallel ? 'parallel' : ''}`}>
                {row.map((node) => {
                  const state = stateOf(node.id);
                  const KindIcon = KIND_ICON[node.kind] || Zap;
                  const StatusIcon = STATUS_ICON[state.status];
                  return (
                    <motion.div
                      key={node.id}
                      className={`wf-node ${state.status} kind-${node.kind}`}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25 }}
                      title={state.detail || node.detail}
                    >
                      <div className="wf-node-top">
                        <KindIcon size={12} className="wf-kind-icon" />
                        <span className="wf-label">{node.label}</span>
                        {StatusIcon && (
                          <StatusIcon
                            size={12}
                            className={`wf-status-icon ${state.status}`}
                          />
                        )}
                      </div>

                      {/* Per-agent bar: indeterminate while running, full when done. */}
                      <div className="wf-bar">
                        <div className={`wf-bar-fill ${state.status}`} />
                      </div>

                      <div className="wf-node-foot">
                        <span className="wf-detail">
                          {state.detail || node.detail}
                        </span>
                        {state.elapsed > 0 && (
                          <span className="wf-elapsed">{state.elapsed}s</span>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WorkflowPanel;
